/**
 * BottomNav — shown on Home, Progress, Settings only (Rule 3).
 * Touch targets ≥ 44×44px (Rule 11 / CLAUDE-final.md).
 * Uses lucide-react (installed dep) — not @phosphor-icons/react.
 *
 * R2 fix (2026-05-09): shows a review-due badge dot on the Home icon when the
 * student has topics due for review, so they notice the queue from any tab.
 */
import { NavLink, useLocation } from 'react-router-dom'
import { Home, BarChart2, Settings } from 'lucide-react'
import { ROUTES }          from '@/lib/routes'
import { cn }              from '@/lib/cn'
import { useReviewCount }  from '@/context/ReviewCountContext'

interface NavItem {
  path:      string
  label:     string
  ariaLabel: string
  Icon:      typeof Home
  showBadge?: boolean
}

export function BottomNav() {
  const location          = useLocation()
  const { dueCount }      = useReviewCount()
  const hasDueTopics      = dueCount > 0

  const NAV_ITEMS: NavItem[] = [
    { path: ROUTES.HOME,     label: 'Home',     ariaLabel: hasDueTopics ? `Go to Home — ${dueCount} topic${dueCount !== 1 ? 's' : ''} due for review` : 'Go to Home', Icon: Home, showBadge: hasDueTopics },
    { path: ROUTES.PROGRESS, label: 'Progress', ariaLabel: 'Go to Progress', Icon: BarChart2 },
    { path: ROUTES.SETTINGS, label: 'Settings', ariaLabel: 'Go to Settings', Icon: Settings  },
  ]

  return (
    <nav aria-label="Main navigation" className="tab-bar">
      {NAV_ITEMS.map(({ path, label, ariaLabel, Icon, showBadge }) => {
        const isActive = location.pathname === path
        return (
          <NavLink
            key={path}
            to={path}
            aria-label={ariaLabel}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex flex-col items-center justify-center gap-1 flex-1',
              'min-h-[44px] min-w-[44px] py-2 transition-colors',
              isActive ? 'text-primary' : 'text-text-muted hover:text-text-body'
            )}
          >
            {/* Icon wrapper — relative so the badge dot can be positioned */}
            <span className="relative">
              <Icon
                size={24}
                aria-hidden="true"
                strokeWidth={isActive ? 2.5 : 1.75}
              />
              {showBadge && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-error"
                  aria-hidden="true"
                />
              )}
            </span>
            <span className={cn(
              'text-xs font-medium',
              isActive ? 'text-primary' : 'text-text-muted'
            )}>
              {label}
            </span>
          </NavLink>
        )
      })}
    </nav>
  )
}
